import * as React from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { cn } from "../../utils/helpers";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

export interface RadixSelectOption {
    value: string;
    label: string;
}

export interface RadixSelectProps {
    options: RadixSelectOption[];
    value: string;
    onValueChange: (value: string) => void;
    onItemSelect?: (value: string) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    contentWidth?: string;
    selectedLabelOverride?: string;
}

const SelectItemWithTooltip = React.forwardRef<HTMLDivElement, {
    opt: RadixSelectOption;
    idx: number;
    onItemSelect?: (value: string) => void;
}>(
    ({ opt, idx, onItemSelect, ...props }, ref) => {
        const textRef = React.useRef<HTMLSpanElement>(null);
        const [isTruncated, setIsTruncated] = React.useState(false);

        const handleMouseEnter = () => {
            if (textRef.current) {
                setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
            }
        };

        const item = (
            <RadixSelect.Item
                {...props}
                value={opt.value}
                className={cn(
                    "cursor-pointer select-none rounded-sm px-2 py-1.5 text-sm font-normal flex items-center justify-between",
                    idx === 0 ? "text-muted-foreground" : "text-foreground",
                    "hover:bg-accent focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent"
                )}
                ref={ref}
                onMouseEnter={handleMouseEnter}
                onPointerUp={() => onItemSelect?.(opt.value)}
            >
                <span className="truncate flex-1 min-w-0" ref={textRef}>
                    <RadixSelect.ItemText>{opt.label}</RadixSelect.ItemText>
                </span>
                <RadixSelect.ItemIndicator>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><polyline points="5 10 9 15 15 6" /></svg>
                </RadixSelect.ItemIndicator>
            </RadixSelect.Item>
        );

        if (!isTruncated) {
            return item;
        }

        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    {item}
                </TooltipTrigger>
                <TooltipContent side="top" align="end" sideOffset={5} alignOffset={-10}>
                    <p>{opt.label}</p>
                </TooltipContent>
            </Tooltip>
        );
    }
);
SelectItemWithTooltip.displayName = "SelectItemWithTooltip";

export const RadixSelectControl: React.FC<RadixSelectProps> = ({
    options,
    value,
    onValueChange,
    onItemSelect,
    className,
    placeholder,
    disabled,
    autoFocus,
    contentWidth,
    selectedLabelOverride,
}) => {
    const [open, setOpen] = React.useState(false);


    React.useEffect(() => {
        if (typeof document === "undefined") return;

        if (open) {
            document.body.classList.add("select-open-keep-scrollbar");
        } else {
            document.body.classList.remove("select-open-keep-scrollbar");
        }

        return () => {
            document.body.classList.remove("select-open-keep-scrollbar");
        };
    }, [open]);

    const isPlaceholder = !value || value === options[0]?.value;
    // Filter out options with empty value
    const filteredOptions = options.filter(opt => opt.value !== "");
    return (
        <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled} open={open} onOpenChange={setOpen}>
            <RadixSelect.Trigger
                autoFocus={autoFocus}
                className={cn(
                    "block w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-sm pr-10 flex items-center shadow-sm relative overflow-hidden whitespace-nowrap box-border outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0",
                    isPlaceholder ? "text-muted-foreground" : "text-foreground",
                    className
                )}
                style={{
                    height: '36px',
                    minHeight: '36px',
                    maxHeight: '36px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                }}
            >
                <span className="truncate">
                    {selectedLabelOverride && !isPlaceholder
                        ? selectedLabelOverride
                        : <RadixSelect.Value placeholder={placeholder || filteredOptions[0]?.label} />}
                </span>
                <RadixSelect.Icon asChild>
                    <span
                        style={{
                            position: 'absolute',
                            right: '0.75rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            pointerEvents: 'none',
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><polyline points="6 9 12 15 18 9" /></svg>
                    </span>
                </RadixSelect.Icon>
            </RadixSelect.Trigger>
            <RadixSelect.Portal>
                <RadixSelect.Content
                    position="popper"
                    sideOffset={4}
                    className="z-[1500] max-h-60 overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-md"
                    style={{ minWidth: contentWidth || 'var(--radix-select-trigger-width)', width: contentWidth }}
                >
                    <RadixSelect.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted-foreground"><path d="M4.18179 8.81819C4.00605 8.99392 3.72113 8.99392 3.54539 8.81819C3.36965 8.64245 3.36965 8.35753 3.54539 8.18179L7.18179 4.54539C7.26615 4.45952 7.38221 4.41162 7.50293 4.41249C7.62365 4.41337 7.73898 4.46287 7.82207 4.54992L11.4585 8.36814C11.63 8.54823 11.6233 8.83351 11.4432 9.00508C11.2631 9.17666 10.9778 9.16999 10.8062 8.98987L7.49479 5.51347L4.18179 8.81819Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
                    </RadixSelect.ScrollUpButton>
                    <RadixSelect.Viewport className="p-1">
                        <TooltipProvider>
                            {filteredOptions.map((opt, idx) => (
                                <SelectItemWithTooltip
                                    key={opt.value}
                                    opt={opt}
                                    idx={idx}
                                    onItemSelect={onItemSelect}
                                />
                            ))}
                        </TooltipProvider>
                    </RadixSelect.Viewport>
                    <RadixSelect.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted-foreground"><path d="M4.18179 6.18181C3.36965 6.35755 3.36965 6.64247 3.54539 6.81821L7.18179 10.4546C7.26615 10.5405 7.38221 10.5884 7.50293 10.5875C7.62365 10.5866 7.73898 10.5371 7.82207 10.4501L11.4585 6.63186C11.63 6.45177 11.6233 6.16649 11.4432 5.99492C11.2631 5.82334 10.9778 5.83001 10.8062 6.01013L7.49479 9.48653L4.18179 6.18181Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
                    </RadixSelect.ScrollDownButton>
                </RadixSelect.Content>
            </RadixSelect.Portal>
        </RadixSelect.Root>
    );
};